// src/scene/ui/index.tsx
/**
 * Standardized UI System
 *
 * Core UI primitives following @react-three/uikit best practices.
 * All reusable components centralized here for consistency.
 *
 * IMPORTANT: Now connected to the unified design system.
 * Design tokens imported from @/design-system for consistency.
 */

import { ReactNode, useState, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { Container, Text, ContainerProperties } from '@react-three/uikit'
import { colors3D, typography, spacing } from '@/design-system'

// ============================================================================
// DESIGN TOKENS (Connected to Design System)
// ============================================================================

/**
 * UI tokens mapped from the unified design system.
 * This maintains backward compatibility while using the new design system.
 */
export const ui = {
  // Colors - using colors3D (Three.js compatible hex numbers)
  colors: {
    // Backgrounds
    bg: {
      deep: colors3D.background.base,        // #09090b
      base: colors3D.background.base,        // #09090b
      elevated: colors3D.background.elevated, // #0f0f18
      surface: colors3D.background.surface,   // #14141f
      card: colors3D.background.overlay,      // #1a1a28
    },
    // Primary accent
    primary: colors3D.brand.primary,           // #648cdc
    primaryMuted: colors3D.interactive.hover,  // Mapped to hover state
    primaryDark: colors3D.interactive.active,  // Mapped to active state
    // Secondary
    secondary: colors3D.brand.secondary,       // #dcb48c
    // Success
    success: colors3D.semantic.success,        // #64b478
    successMuted: colors3D.interactive.hover,  // Mapped to hover state
    // Text hierarchy
    text: {
      primary: colors3D.text.primary,    // #fafafa
      secondary: colors3D.text.secondary, // #b0b0b8
      tertiary: colors3D.text.tertiary,   // #808088
      muted: colors3D.text.disabled,      // #505058
      accent: colors3D.text.accent,       // #648cdc
    },
    // Borders
    border: {
      subtle: colors3D.border.subtle,   // #1f1f2a
      light: colors3D.border.default,   // #2a2a38
      medium: colors3D.border.strong,   // #3a3a48
      accent: colors3D.border.accent,   // #648cdc
    },
  },

  // Typography - using typography from design system
  font: {
    size: {
      xs: typography.fontSize.xs,      // 12
      sm: typography.fontSize.sm,      // 14
      md: typography.fontSize.base,    // 16
      lg: typography.fontSize.lg,      // 18
      xl: typography.fontSize.xl,      // 24
      '2xl': typography.fontSize['2xl'], // 32
      '3xl': typography.fontSize['3xl'], // 48
    },
    weight: {
      normal: typography.fontWeight.medium,     // 500 (minimum medium for readability)
      medium: typography.fontWeight.medium,     // 500
      semibold: typography.fontWeight.semibold, // 600
      bold: typography.fontWeight.bold,         // 700
    },
    leading: {
      tight: typography.lineHeight.tight,     // 1.2
      normal: typography.lineHeight.normal,   // 1.5
      relaxed: typography.lineHeight.relaxed, // 1.75
    },
  },

  // Spacing scale - using spacing from design system
  space: {
    xs: spacing[1],  // 4px
    sm: spacing[2],  // 8px
    md: spacing[3],  // 12px
    lg: spacing[4],  // 16px
    xl: spacing[6],  // 24px
    '2xl': spacing[8], // 32px
  },

  // Border radius (custom for 3D UI)
  radius: {
    sm: 6,
    md: 10,
    lg: 14,
    xl: 18,
    full: 9999,
  },
} as const

// ============================================================================
// BASE TEXT COMPONENTS
// ============================================================================

interface TextProps {
  children: ReactNode
  color?: number | string
  align?: 'left' | 'center' | 'right'
}

export function Title({ children, color = ui.colors.text.primary, align = 'left' }: TextProps) {
  return (
    <Text
      fontSize={ui.font.size.xl}
      fontWeight={`${ui.font.weight.bold}` as any}
      color={color}
      textAlign={align}
      lineHeight={ui.font.leading.tight}
    >
      {children}
    </Text>
  )
}

export function Heading({ children, color = ui.colors.text.primary, align = 'left' }: TextProps) {
  return (
    <Text
      fontSize={ui.font.size.lg}
      fontWeight={`${ui.font.weight.bold}` as any}
      color={color}
      textAlign={align}
      lineHeight={ui.font.leading.tight}
    >
      {children}
    </Text>
  )
}

export function Subheading({ children, color = ui.colors.text.secondary, align = 'left' }: TextProps) {
  return (
    <Text
      fontSize={ui.font.size.md}
      fontWeight={`${ui.font.weight.semibold}` as any}
      color={color}
      textAlign={align}
      lineHeight={ui.font.leading.normal}
    >
      {children}
    </Text>
  )
}

export function Body({ children, color = ui.colors.text.secondary, align = 'left' }: TextProps) {
  return (
    <Text
      fontSize={ui.font.size.sm}
      fontWeight={`${ui.font.weight.normal}` as any}
      color={color}
      textAlign={align}
      lineHeight={ui.font.leading.relaxed}
    >
      {children}
    </Text>
  )
}

export function Caption({ children, color = ui.colors.text.tertiary, align = 'left' }: TextProps) {
  return (
    <Text
      fontSize={ui.font.size.xs}
      fontWeight={`${ui.font.weight.normal}` as any}
      color={color}
      textAlign={align}
      lineHeight={ui.font.leading.normal}
    >
      {children}
    </Text>
  )
}

export function Label({ children, color = ui.colors.text.accent, align = 'left' }: TextProps) {
  return (
    <Text
      fontSize={ui.font.size.xs}
      fontWeight={`${ui.font.weight.semibold}` as any}
      color={color}
      textAlign={align}
      letterSpacing={0.5}
    >
      {children}
    </Text>
  )
}

// ============================================================================
// LAYOUT COMPONENTS
// ============================================================================

interface StackProps {
  children: ReactNode
  gap?: number
  align?: 'flex-start' | 'center' | 'flex-end' | 'stretch'
  justify?: 'flex-start' | 'center' | 'flex-end' | 'space-between'
  padding?: number
  width?: number | `${number}%`
}

export function VStack({
  children,
  gap = ui.space.md,
  align = 'stretch',
  justify = 'flex-start',
  padding,
  width,
}: StackProps) {
  return (
    <Container
      flexDirection="column"
      gap={gap}
      alignItems={align}
      justifyContent={justify}
      padding={padding}
      width={width}
    >
      {children}
    </Container>
  )
}

export function HStack({
  children,
  gap = ui.space.md,
  align = 'center',
  justify = 'flex-start',
  padding,
  width,
}: StackProps) {
  return (
    <Container
      flexDirection="row"
      gap={gap}
      alignItems={align}
      justifyContent={justify}
      padding={padding}
      width={width}
    >
      {children}
    </Container>
  )
}

export function Center({ children, padding }: { children: ReactNode; padding?: number }) {
  return (
    <Container
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      padding={padding}
    >
      {children}
    </Container>
  )
}

// ============================================================================
// HOVER SCALE HOOK
// ============================================================================

export function useHoverScale(target = 1.03, speed = 8) {
  const [hovered, setHovered] = useState(false)
  const [pressed, setPressed] = useState(false)
  const scaleRef = useRef(1)
  const [scale, setScale] = useState(1)
  const holdTimerRef = useRef<ReturnType<typeof setTimeout>>()

  const isActive = hovered || pressed

  useFrame((_, delta) => {
    const dest = isActive ? target : 1
    const prev = scaleRef.current
    const diff = dest - prev
    if (Math.abs(diff) < 0.0005) {
      if (prev !== dest) {
        scaleRef.current = dest
        setScale(dest)
      }
      return
    }
    scaleRef.current = prev + diff * Math.min(1, delta * speed)
    setScale(scaleRef.current)
  })

  return {
    /** Whether the element is hovered or pressed (use for conditional styling) */
    isActive,
    transformScaleX: scale,
    transformScaleY: scale,
    onPointerEnter: () => setHovered(true),
    onPointerLeave: () => setHovered(false),
    onPointerDown: () => {
      clearTimeout(holdTimerRef.current)
      setPressed(true)
    },
    onPointerUp: () => {
      // Hold the press effect briefly so quick taps are visible on touch devices
      holdTimerRef.current = setTimeout(() => setPressed(false), 150)
    },
  }
}

// ============================================================================
// CARD COMPONENTS
// ============================================================================

interface CardProps {
  children: ReactNode
  padding?: number
  width?: number | `${number}%`
  height?: number | `${number}%`
  accentColor?: string | number
  variant?: 'default' | 'elevated' | 'outline'
  hover?: ContainerProperties['hover']
  transformScaleX?: number
  transformScaleY?: number
  onPointerEnter?: () => void
  onPointerLeave?: () => void
  onPointerDown?: () => void
  onPointerUp?: () => void
}

export function Card({
  children,
  padding = ui.space.lg,
  width,
  accentColor,
  variant = 'default',
  hover,
  transformScaleX,
  transformScaleY,
  onPointerEnter,
  onPointerLeave,
  onPointerDown,
  onPointerUp,
}: CardProps) {
  const bgColor = variant === 'elevated' ? ui.colors.bg.card : ui.colors.bg.surface
  const borderColor = accentColor ?? ui.colors.border.subtle

  return (
    <Container
      flexDirection="column"
      padding={padding}
      gap={ui.space.sm}
      width={width}
      backgroundColor={bgColor}
      borderRadius={ui.radius.lg}
      borderWidth={1}
      borderLeftWidth={accentColor ? 3 : 1}
      borderColor={accentColor ?? borderColor}
      hover={hover}
      transformScaleX={transformScaleX}
      transformScaleY={transformScaleY}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    >
      {children}
    </Container>
  )
}

export function Panel({
  children,
  padding = ui.space.xl,
  width,
  height,
}: CardProps) {
  return (
    <Container
      flexDirection="column"
      paddingTop={padding}
      paddingBottom={padding}
      paddingLeft={padding}
      paddingRight={padding}
      gap={ui.space.lg}
      width={width}
      height={height}
      backgroundColor={ui.colors.bg.elevated}
      borderRadius={ui.radius.xl}
      borderWidth={1}
      borderColor={ui.colors.border.light}
    >
      {children}
    </Container>
  )
}

// ============================================================================
// BADGE & TAG COMPONENTS
// ============================================================================

interface BadgeProps {
  children: ReactNode
  variant?: 'default' | 'success' | 'accent' | 'muted'
}

export function Badge({ children, variant = 'default' }: BadgeProps) {
  const styles = {
    default: { bg: ui.colors.bg.card, text: ui.colors.text.secondary, border: ui.colors.border.medium },
    success: { bg: '#1a3a2a', text: ui.colors.success, border: ui.colors.success },
    accent: { bg: '#1a2a4a', text: ui.colors.primary, border: ui.colors.primary },
    muted: { bg: ui.colors.bg.surface, text: ui.colors.text.muted, border: ui.colors.border.subtle },
  }

  const style = styles[variant]

  return (
    <Container
      backgroundColor={style.bg}
      paddingX={ui.space.sm}
      paddingY={ui.space.xs}
      borderRadius={ui.radius.full}
      borderWidth={1}
      borderColor={style.border}
      alignItems="center"
      justifyContent="center"
      flexDirection="row"
    >
      <Text
        fontSize={ui.font.size.xs}
        fontWeight={`${ui.font.weight.semibold}` as any}
        color={style.text}
        textAlign="center"
        wordBreak="keep-all"
      >
        {children}
      </Text>
    </Container>
  )
}

interface TagProps {
  children: ReactNode
  color?: number | string
}

export function Tag({ children, color = ui.colors.primary }: TagProps) {
  return (
    <Container
      backgroundColor={ui.colors.bg.card}
      paddingX={ui.space.sm}
      paddingY={ui.space.xs}
      borderRadius={ui.radius.sm}
      borderWidth={1}
      borderColor={ui.colors.border.light}
    >
      <Text
        fontSize={ui.font.size.xs}
        fontWeight={`${ui.font.weight.medium}` as any}
        color={color}
      >
        {children}
      </Text>
    </Container>
  )
}

// ============================================================================
// PROGRESS BAR
// ============================================================================

interface ProgressProps {
  value: number
  label?: string
  showValue?: boolean
}

export function Progress({ value, label, showValue = true }: ProgressProps) {
  const clampedValue = Math.min(100, Math.max(0, value))

  return (
    <VStack gap={ui.space.xs} width="100%">
      {(label || showValue) && (
        <HStack justify="space-between" width="100%">
          {label && <Body>{label}</Body>}
          {showValue && <Caption>{clampedValue}%</Caption>}
        </HStack>
      )}
      <Container
        width="100%"
        height={6}
        backgroundColor={ui.colors.border.light}
        borderRadius={ui.radius.full}
        overflow="hidden"
      >
        <Container
          width={`${clampedValue}%`}
          height="100%"
          backgroundColor={ui.colors.primary}
          borderRadius={ui.radius.full}
        />
      </Container>
    </VStack>
  )
}

// ============================================================================
// DIVIDER
// ============================================================================

export function Divider({ color = ui.colors.border.subtle }: { color?: number | string }) {
  return (
    <Container
      width="100%"
      height={1}
      backgroundColor={color}
    />
  )
}

// ============================================================================
// SECTION COMPONENT
// ============================================================================

interface SectionProps {
  title: string
  children: ReactNode
}

export function Section({ title, children }: SectionProps) {
  return (
    <VStack gap={ui.space.md}>
      <Subheading color={ui.colors.text.primary}>{title}</Subheading>
      {children}
    </VStack>
  )
}

// ============================================================================
// PANEL HEADER (for stage panels)
// ============================================================================

interface PanelHeaderProps {
  title: string
  badge?: ReactNode
}

export function PanelHeader({ title, badge }: PanelHeaderProps) {
  return (
    <Container
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      paddingBottom={ui.space.md}
      borderBottomWidth={1}
      borderColor={ui.colors.border.subtle}
    >
      <Text
        fontSize={ui.font.size.lg}
        fontWeight={`${ui.font.weight.bold}` as any}
        color={ui.colors.primary}
      >
        {title}
      </Text>
      {badge}
    </Container>
  )
}

// ============================================================================
// LIST ITEM
// ============================================================================

interface ListItemProps {
  children: ReactNode
  bullet?: string
}

export function ListItem({ children, bullet = '-' }: ListItemProps) {
  return (
    <HStack gap={ui.space.sm} align="flex-start">
      <Caption color={ui.colors.text.secondary}>{bullet}</Caption>
      <Caption color={ui.colors.text.secondary}>{children}</Caption>
    </HStack>
  )
}

// ============================================================================
// EXPORTS
// ============================================================================

export { Container, Text } from '@react-three/uikit'
